import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IconSettings } from '@tabler/icons-react';
import { ButtonContent } from './ui/ButtonContent';
import { MicIcon } from './icons/MicIcon';
import { getMediaStreamWithFallback } from '../lib/media-utils';
import { captureException } from '../lib/telemetry/posthog/frontend';
import { getAudioContextConstructor } from '../lib/utils/audio-context';
import { computeMicLevel, smoothMicLevel } from '../lib/utils/audio-level';
import { useMediaPermission } from './hooks/useMediaPermission';

interface MicrophoneSetupProps {
  onDeviceSelect: (deviceId: string) => void;
  onSpeakerSelect?: (deviceId: string) => void;
  /**
   * Defer the permission prompt until the access requester is invoked (for
   * example on the Start button), instead of prompting on mount.
   */
  deferPermission?: boolean;
  /**
   * Receives the imperative access requester so a parent Start control can
   * trigger the prompt on a user gesture. Resolves true once access is granted.
   * (Audio-only, so the optional `skipVideo` arg is ignored here.)
   */
  onAccessRequesterReady?: (request: (opts?: { skipVideo?: boolean }) => Promise<boolean>) => void;
}

const MicrophoneSetup: React.FC<MicrophoneSetupProps> = ({
  onDeviceSelect,
  deferPermission = false,
  onAccessRequesterReady,
}) => {
  const {
    devices,
    selectedId,
    setSelectedId,
    permissionError,
    permissionDismissed,
    unsupportedEnvironment,
    retryHint,
    isRetrying,
    granted,
    requestAccess,
    handleEnableAccess,
    handleRetryFromDismissed,
  } = useMediaPermission({
    onDeviceSelect,
    context: 'MicrophoneSetup',
    deferUntilRequested: deferPermission,
  });

  const [volume, setVolume] = useState(0);
  const [showTestModal, setShowTestModal] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);

  // Audio visualization stream
  useEffect(() => {
    if (!selectedId) return undefined;

    /** Set to true by the cleanup function so an in-flight getUserMedia result
     * knows the component is already gone and must not keep the mic open. */
    let cancelled = false;

    const startStream = async () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (audioContextRef.current) void audioContextRef.current.close();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      try {
        const result = await getMediaStreamWithFallback({
          deviceId: selectedId,
          kind: 'audio',
          constraints: {
            autoGainControl: false,
            echoCancellation: false,
            noiseSuppression: false,
          },
          context: 'microphone_setup_metering',
        });

        // Component unmounted while the promise was in flight: stop every
        // acquired track immediately so the mic/camera light does not linger.
        if (cancelled) {
          result.stream.getTracks().forEach((t) => t.stop());
          return;
        }

        const stream = result.stream;
        streamRef.current = stream;

        if (result.fallbackUsed && result.actualDeviceId) {
          setSelectedId(result.actualDeviceId);
        }

        const ctx = new (getAudioContextConstructor())();
        audioContextRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.5;
        source.connect(analyser);

        // Time-domain samples drive an RMS level (see computeMicLevel).
        const dataArray = new Uint8Array(analyser.fftSize);

        const animate = () => {
          if (cancelled) return;
          analyser.getByteTimeDomainData(dataArray);
          const level = computeMicLevel(dataArray);
          setVolume((prev) => smoothMicLevel(prev, level));
          rafRef.current = requestAnimationFrame(animate);
        };
        animate();
      } catch (e) {
        console.error('Error accessing microphone for setup:', e);
        captureException(e instanceof Error ? e : String(e), 'MicrophoneSetup.getAccess', { selectedId });
      }
    };
    void startStream();

    return () => {
      cancelled = true;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        void audioContextRef.current.close();
      }
    };
  }, [selectedId, setSelectedId]);

  // Hand the access requester to the parent so the Start gesture can prompt.
  useEffect(() => {
    onAccessRequesterReady?.(requestAccess);
  }, [onAccessRequesterReady, requestAccess]);

  /**
   * Opens the device test modal, first prompting for access if it has not yet
   * been granted (deferred mode). Testing devices is an explicit user action,
   * so requesting permission here does not reintroduce on-landing friction.
   */
  const handleOpenTestModal = useCallback(async () => {
    if (!granted) {
      const ok = await requestAccess();
      if (!ok) return;
    }
    setShowTestModal(true);
  }, [granted, requestAccess]);

  if (unsupportedEnvironment) {
    return (
      <div className="w-full">
        <div className="flex flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="text-lg text-muted-foreground">
            Microphone access isn't supported in your current browser environment.
            Please open this link directly in Chrome or Firefox to continue.
          </p>
        </div>
      </div>
    );
  }

  if (permissionDismissed) {
    return (
      <div className="w-full">
        <div className="flex flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="text-lg text-muted-foreground">
            Microphone access is needed to start.
          </p>
          <button
            type="button"
            onClick={handleRetryFromDismissed}
            className="px-4 py-2 rounded-lg bg-volt/15 hover:bg-volt/15 text-foreground text-sm transition-colors"
          >
            Allow microphone access
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {permissionError ? (
        <div className="bg-wash-danger rounded-lg p-3 text-foreground space-y-3">
          <p className="text-lg font-medium">Microphone access is required to join.</p>
          <p className="text-muted-foreground text-sm">Enable access in your browser, then retry.</p>
          {retryHint && (
            <p className="text-muted-foreground text-sm">{retryHint}</p>
          )}
          <button
            type="button"
            onClick={handleEnableAccess}
            disabled={isRetrying}
            aria-busy={isRetrying}
            className="px-3 py-2 rounded-lg button-accent font-medium disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <ButtonContent loading={isRetrying}>Enable Access</ButtonContent>
          </button>
        </div>
      ) : (
        /* Quiet settings affordance pinned to the card's lower corner: a cog
           plus a mic glyph that opens the device test panel. */
        <button
          type="button"
          onClick={() => { void handleOpenTestModal(); }}
          aria-label="Test devices"
          title="Test devices"
          className="absolute bottom-4 right-4 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-hover/60 transition-colors"
        >
          <IconSettings className="w-5 h-5" aria-hidden="true" />
          <MicIcon className="w-5 h-5" aria-hidden="true" />
        </button>
      )}

      {/* Test Devices Modal */}
      {showTestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="w-full max-w-md bg-background border border-border rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-foreground">Test Your Devices</h3>
              <button
                type="button"
                onClick={() => setShowTestModal(false)}
                aria-label="Close"
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Mic selector */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2 text-left">Microphone</label>
                <div className="relative">
                  <select
                    value={selectedId}
                    onChange={(e) => setSelectedId(e.target.value)}
                    className="w-full bg-background border border-border text-muted-foreground rounded-lg px-4 py-3 pl-10 appearance-none focus:ring-1 focus:ring-volt focus:border-volt focus:outline-none cursor-pointer transition-colors"
                  >
                    {devices.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label || `Microphone ${d.deviceId.slice(0, 5)}...`}
                      </option>
                    ))}
                  </select>
                  <MicIcon className="absolute left-3 top-3.5 w-5 h-5 text-subtle-foreground pointer-events-none" />
                </div>
              </div>

              {/* Audio level visualizer */}
              <div>
                <div className="text-xs text-subtle-foreground mb-1 text-left">
                  Input Level
                </div>
                <div className="h-2 bg-surface-raised rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-colors duration-100 ease-out ${volume > 80 ? 'bg-danger' : 'bg-success'}`}
                    style={{ width: `${volume}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MicrophoneSetup;
